import React, { useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { Card, CardBody, Col, Container, Row } from "reactstrap"
import PageTitle from "../../../components/Common/PageTitle"
import Spinners from "components/Common/Spinner"
import UserService from "Api/Services/UserService"
import DoctorSelector from "components/Common/DoctorSelector"
import {
  getStoredSelectedDoctorId,
  isGroupLoginUser,
  setStoredSelectedDoctorId,
} from "helpers/doctorSelection"

const AssignDrugListOption = () => {
  document.title = "Drug Form | Infuse One"

  const params = useParams()
  const [isLoading, setLoading] = useState(false)
  const [userDrugForm, setUsersDrugForm] = useState([])
  const [error, setError] = useState("")
  const [formMapping, setFormMapping] = useState([])
  const [userDetail, setUserDetail] = useState(null)
  const [doctors, setDoctors] = useState([])
  const [selectedDoctorId, setSelectedDoctorId] = useState(getStoredSelectedDoctorId())
  const [groupLogin] = useState(isGroupLoginUser())

  const loadUserDrugDetail = async doctorId => {
    setLoading(true)

    try {
      const response = await UserService.ShowUserdrugDetailForm(params.id, doctorId).json()

      if (response.status === true) {
        setUsersDrugForm(response.data || {})
        setUserDetail(response.selected_doctor || null)
        setError("")
        setLoading(false)
        return
      }

      setLoading(false)
      setError(response.message)
    } catch (error) {
      setLoading(false)
      setError("Something went wrong")
    }
  }

  const loadUserDetail = async doctorId => {
    try {
      const response = await UserService.GetUserForForm(doctorId)
      const data = await response.json()

      if (data.status) {
        setUserDetail(data)
      } else {
        console.error("User API error:", data.message)
      }
    } catch (error) {
      console.error("User detail API error:", error)
    }
  }

  const loadAccessibleDoctors = async () => {
    if (!groupLogin) {
      loadUserDrugDetail()
      loadUserDetail()
      return
    }

    try {
      const response = await UserService.GetAccessibleDoctors().json()

      if (!response.status) {
        setError(response.message || "Something went wrong")
        return
      }

      const availableDoctors = response.data || []
      const fallbackDoctorId = response.selected_doctor?.id ? String(response.selected_doctor.id) : ""
      const storedDoctorId = getStoredSelectedDoctorId()
      const nextDoctorId = availableDoctors.some(doctor => String(doctor.id) === storedDoctorId)
        ? storedDoctorId
        : fallbackDoctorId

      setDoctors(availableDoctors)
      setSelectedDoctorId(nextDoctorId)
      setStoredSelectedDoctorId(nextDoctorId)
      loadUserDrugDetail(nextDoctorId)
    } catch (error) {
      setError("Something went wrong")
    }
  }

  const loadFormMapping = async formId => {
    try {
      const response = await UserService.GetFormData(formId)
      const data = await response.json()

      if (data.status) {
        setFormMapping(data.data)
      } else {
        console.error("Form mapping not found:", data.message)
      }
    } catch (error) {
      console.error("Form mapping error:", error)
    }
  }

  useEffect(() => {
    loadAccessibleDoctors()
  }, [params.id])

  useEffect(() => {
    if (!groupLogin || !selectedDoctorId) {
      return
    }

    setStoredSelectedDoctorId(selectedDoctorId)
    loadUserDrugDetail(selectedDoctorId)
  }, [selectedDoctorId, groupLogin, params.id])

  useEffect(() => {
    if (userDrugForm?.id) {
      loadFormMapping(userDrugForm.id)
    }
  }, [userDrugForm])

  const buildPrefillIframeUrl = () => {
    if (!userDrugForm?.form_iframe || !userDetail) {
      return userDrugForm?.form_iframe || ""
    }

    try {
      const url = new URL(userDrugForm.form_iframe)

      formMapping.forEach(map => {
        const value = userDetail[map.field_type]
        if (value) {
          url.searchParams.set(map.field_id, value)
        }
      })

      return url.toString()
    } catch (error) {
      console.warn("Error building iframe URL:", error)
      return userDrugForm.form_iframe
    }
  }

  const iframeUrl = buildPrefillIframeUrl()

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div className="container-fluid">
            <PageTitle
              title="Drug Form"
              actions={groupLogin ? (
                <DoctorSelector
                  doctors={doctors}
                  selectedDoctorId={selectedDoctorId}
                  onChange={setSelectedDoctorId}
                  compact
                />
              ) : null}
            />

            {isLoading ? (
              <Spinners setLoading={setLoading} />
            ) : (
              <Row>
                {error ? (
                  <div className="bg-white p-5 rounded">
                    <h5 className="text-center">{error}</h5>
                  </div>
                ) : (
                  <Col xl={12} sm={12}>
                    <Card className="text-center">
                      <CardBody>
                        <div className="mb-4 d-flex justify-content-between align-items-center">
                          <h5 className="h2 mb-2 mb-3">{userDrugForm.name}</h5>
                          <Link to="/assgined-user-drug-list" className="btn btn-secondary">
                            Go Back
                          </Link>
                        </div>

                        <div className="embed-responsive embed-responsive-16by9 ratio ratio-16x9">
                          {iframeUrl ? (
                            <iframe
                              title={userDrugForm?.name || "Drug Form"}
                              className="embed-responsive-item"
                              src={iframeUrl}
                              style={{
                                border: "none",
                                width: "100%",
                                height: "600px",
                              }}
                            />
                          ) : (
                            <p>Form URL not available</p>
                          )}
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                )}
              </Row>
            )}
          </div>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default AssignDrugListOption
